import React, { useState } from 'react';
import { HashRouter as Router, useLocation } from 'react-router-dom';
import { AppBar, Box, CssBaseline, Toolbar } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import initializeApis from './services/initializeApis';
import TopNavigation from './components/menu/TopNavigation';
import SideNavigation from './components/menu/SideNavigation';
import Logo from './components/menu/shared/Logo';
import { NavigationProvider, useNavigation } from './contexts/NavigationProvider';
import { ThemeProvider } from './contexts/ThemeProvider';
import { UniversityConfigProvider, useUniversityConfig } from './contexts/UniversityConfigProvider';
import MainContent from './routes/MainContent';
import { shouldHideNavigation } from './configs/routeConfig';
import ErrorFallback from './components/ErrorFallback';
import AppLoader from './components/common/AppLoader';
import FeedbackModal from './components/shared/FeedbackWidget/FeedbackModal';
import { AuthProvider, useAuth } from './contexts/AuthProvider';
import SurveyModal from './components/shared/SurveyWidget/SurveyModal';
import "./index.css";

initializeApis();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { isSideNav } = useNavigation();
  const { isAuthenticated } = useAuth();
  const { config } = useUniversityConfig();
  const location = useLocation();

  const routeHidden = shouldHideNavigation(location.pathname) || !isAuthenticated;
  const navHidden = routeHidden || !!config?.hideNavigation;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!navHidden && (isSideNav ? <SideNavigation /> : <TopNavigation />)}
      {!routeHidden && !!config?.hideNavigation && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: '63px !important' }}>
            <Logo variant="header" sx={{ height: 'auto' }} />
          </Toolbar>
        </AppBar>
      )}
      {children}
    </Box>
  );
};

const AppContent: React.FC = () => {
  const { config, isLoading: configLoading, error: configError } = useUniversityConfig();
  const { user, isLoading: authLoading, error: authError, isAuthenticated } = useAuth();
  const [userReady, setUserReady] = useState<boolean>(false);

  const [isSurveyOpen, setIsSurveyOpen] = useState<boolean>(false);

  const isLoading = configLoading || authLoading;
  const error = configError || authError;

  React.useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
      setUserReady(true);
    } else {
      setUserReady(false);
    }
  }, [isAuthenticated, user, isLoading]);

  React.useEffect(() => {
    const hideInitialLoader = () => {
      const initialLoader = document.getElementById('initial-loader');
      if (initialLoader) {
        initialLoader.classList.add('fade-out');
        setTimeout(() => {
          initialLoader.remove();
        }, 300);
      }
    };

    if (!isLoading && config) {
      hideInitialLoader();
    } else if (!isLoading && !config && !error) {
      hideInitialLoader();
    }
  }, [isLoading, config, error]);

  if (error || (!config && !isLoading)) {
    return <ErrorFallback error={error || undefined} />;
  }

  return (
    <ThemeProvider>
      {isLoading ? (
        <AppLoader message="Setting up Application" variant="default" size={50} fullScreen={true} />
      ) : (
        <NavigationProvider user={user}>
          <FeedbackModal appLoaded={!isLoading && !!config} isSurveyOpen={isSurveyOpen} />
          <SurveyModal appLoaded={!isLoading && !!config} onSurveyStateChange={setIsSurveyOpen} user={user} userReady={userReady} />

          <AppLayout>
            <MainContent />
          </AppLayout>
        </NavigationProvider>
      )}
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <UniversityConfigProvider>
        <Router>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
      </UniversityConfigProvider>
    </QueryClientProvider>
  );
};

export default App;
